import React from 'react';
import { Modal, Form, Input, message, Row, Col } from 'antd';
import { useCreateRailwayStationMutation, useUpdateRailwayStationMutation, RailwayStation } from '../../store/api/referencesApi';

export interface CreateRailwayStationFormData {
  name: string;
  code?: string;
  city?: string;
  region?: string;
  country?: string;
}

interface CreateRailwayStationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (station: RailwayStation) => void;
  station?: RailwayStation | null; // Для режима редактирования
  title?: string;
}

const CreateRailwayStationModal: React.FC<CreateRailwayStationModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  station = null,
  title = 'Добавить железнодорожную станцию'
}) => {
  const [form] = Form.useForm();
  const [createRailwayStation, { isLoading: isCreating }] = useCreateRailwayStationMutation();
  const [updateRailwayStation, { isLoading: isUpdating }] = useUpdateRailwayStationMutation();

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!station;

  const handleSubmit = async (values: CreateRailwayStationFormData) => {
    try {
      console.log('🚂 Creating/Updating railway station:', values);
      
      if (isEditMode && station) {
        const updatedStation = await updateRailwayStation({
          id: station.id,
          data: {
            ...values,
            country: values.country || 'Россия'
          }
        }).unwrap();
        
        console.log('✅ Railway station updated successfully:', updatedStation);
        message.success('Железнодорожная станция успешно обновлена');
        onSuccess(updatedStation);
      } else {
        const newStation = await createRailwayStation({
          ...values,
          country: values.country || 'Россия'
        }).unwrap();
        
        console.log('✅ Railway station created successfully:', newStation);
        message.success('Железнодорожная станция успешно создана');
        onSuccess(newStation);
      }
      
      form.resetFields();
      onCancel();
    } catch (error: any) {
      console.error('❌ Error creating/updating railway station:', error);
      const errorMessage = error?.data?.error || 'Ошибка при создании/обновлении железнодорожной станции';
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Заполняем форму данными для редактирования
  React.useEffect(() => {
    if (visible && station) {
      form.setFieldsValue({
        name: station.name,
        code: station.code,
        city: station.city,
        region: station.region,
        country: station.country
      });
    } else if (visible && !station) {
      form.resetFields();
      form.setFieldsValue({ country: 'Россия' });
    }
  }, [visible, station, form]);

  return (
    <Modal
      title={isEditMode ? 'Редактировать железнодорожную станцию' : title}
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText={isEditMode ? 'Обновить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={isLoading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ country: 'Россия' }}
      >
        <Form.Item
          label="Название станции"
          name="name"
          rules={[
            { required: true, message: 'Введите название станции' },
            { min: 2, message: 'Название должно содержать минимум 2 символа' }
          ]}
        >
          <Input placeholder="Например: Москва-Пассажирская" maxLength={255} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Код станции"
              name="code"
            >
              <Input 
                placeholder="Код станции"
                maxLength={10}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Город"
              name="city"
            >
              <Input 
                placeholder="Название города"
                maxLength={100}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Регион"
              name="region"
            >
              <Input 
                placeholder="Область/край/республика"
                maxLength={100}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Страна"
              name="country"
            >
              <Input 
                placeholder="Страна"
                maxLength={100}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateRailwayStationModal; 